import { useState, useEffect } from 'react';
import { postRequest } from '../../utils/networking';
import { apiUrls, imageUrl as imgUrl } from '../../utils/url';
import { toast } from 'react-toastify';
import { Calendar, Loader2 } from 'lucide-react';
import Pagination from '../../components/Pagination';
import CommonToastContainer from '../../components/CommonToastContainer';

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }

  // If it contains "asstes/" (common typo), extract path after it
  const asstesIndex = normalized.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return normalized.substring(asstesIndex + 7);
  }
  
  // If it's an absolute Windows path but not in assets, we might just want the filename
  if (normalized.includes(":/")) {
    return normalized.split('/').pop();
  }
  
  // Otherwise, assume it's a relative path and return as is (normalized)
  return normalized;
}

const getProcessedImageUrl = (imageUrlSource) => {
  const FALLBACK_IMAGE = 'https://via.placeholder.com/40?text=No+Image';

  if (!imageUrlSource) return FALLBACK_IMAGE;

  // If it's already a full URL or data URL, return as is
  if (imageUrlSource.startsWith('http') || imageUrlSource.startsWith('data:image')) {
    return imageUrlSource;
  }

  // Extract path using getPath function
  const extractedPath = getPath(imageUrlSource);
  
  if (!extractedPath) {
    // If getPath returns null, try using the original value
    if (imageUrlSource) {
      // Remove any leading slashes and join with imgUrl
      const cleanPath = imageUrlSource.replace(/^\/+/, '');
      return `${imgUrl}/${cleanPath}`;
    }
    return FALLBACK_IMAGE;
  }

  // Remove leading slash if present and join with imgUrl
  const cleanPath = extractedPath.replace(/^\/+/, '');
  const finalUrl = `${imgUrl}/${cleanPath}`;
  
  console.log('Processing image URL:', {
    original: imageUrlSource,
    extractedPath,
    cleanPath,
    finalUrl
  });
  
  return finalUrl;
};

const stripHtml = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  } catch (e) {
    return html.replace(/<[^>]*>/g, '');
  }
};

export default function WebinarHistory() {
  const [allWebinars, setAllWebinars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [imageErrors, setImageErrors] = useState({}); // Track image loading errors

  const totalItems = allWebinars.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWebinars = allWebinars.slice(startIndex, endIndex);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';

    const date = new Date(dateString);
    if (isNaN(date)) return 'N/A';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 'Free') return 'Free';
    if (price === '0' || price === 0) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleImageError = (webinarId) => {
    if (!imageErrors[webinarId]) {
      setImageErrors(prev => ({
        ...prev,
        [webinarId]: true
      }));
    }
  };

  const fetchAllWebinars = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await postRequest(apiUrls.gethistorywebinar, {
        page: 1,
        limit: 1000
      });

      console.log('Webinar history response:', response);

      if (response.status === 'S' && response.data) {
        let historyData = [];

        if (Array.isArray(response.data)) {
          historyData = response.data;
        } else if (response.data.webinars && Array.isArray(response.data.webinars)) {
          historyData = response.data.webinars;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          historyData = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          historyData = response.data.data;
        } else {
          historyData = [];
        }

        const formattedHistory = historyData.map((item, index) => {
          // Get the mentor profile URL from the response
          const mentorProfileUrl = item.mentorprofileurl || item.mentorProfileImage || item.mentorimg;
          
          // Process the image URL using getProcessedImageUrl
          let processedImageUrl = getProcessedImageUrl(mentorProfileUrl);
          
          // Get price from various possible field names
          const price = item.price || item.webinarprice || item.cost || item.amount || 0;
          
          // Get shared info from various possible field names
          const sharedInfo = item.sharedinfo || item.sharedInfo || item.additionalInfo || item.notes || item.description_extra || '';

          console.log(`Processing image for webinar ${item.webinarname || item.title}:`, {
            original: mentorProfileUrl,
            processed: processedImageUrl
          });

          return {
            id: item.id || index + 1,
            title: item.webinarname || item.title || 'Untitled Webinar',
            description: item.webinardescription || item.description || 'No description available',
            mentorName: item.mentorname || item.mentorName || 'Unknown Mentor',
            mentorProfileImage: processedImageUrl,
            mentorProfileOriginal: mentorProfileUrl, // Store original for debugging
            webinarOn: item.asondate || item.webinarOn || new Date().toISOString(),
            price: price,
            sharedInfo: sharedInfo
          };
        });

        setAllWebinars(formattedHistory);
      } else {
        const errorMsg = response.message || 'Failed to fetch webinar history';
        setError(errorMsg);
        toast.error(errorMsg);
        setAllWebinars([]);
      }
    } catch (err) {
      console.error('Error fetching webinar history:', err);
      const errorMsg = 'Unable to load webinar history. Please try again later.';
      setError(errorMsg);
      toast.error(errorMsg);
      setAllWebinars([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const mainContainer = document.querySelector('.webinar-history-container');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchAllWebinars();
  }, []);

  const getContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Loader2 className="inline-block animate-spin h-6 w-6 text-amber-500 mb-2" />
            <p className="text-sm text-gray-600">Loading webinar history...</p>
          </div>
        </div>
      );
    }

    if (error && allWebinars.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <h3 className="text-base font-semibold text-gray-700 mb-1">Failed to load webinar history</h3>
          <p className="text-xs text-gray-500 mb-3">{error}</p>
          <button
            onClick={fetchAllWebinars}
            className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (allWebinars.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <Calendar className="w-12 h-12 text-gray-400 mb-3" />
          <h3 className="text-base font-semibold text-gray-700 mb-1">No webinar history found</h3>
          <p className="text-xs text-gray-500">Past webinars will appear here</p>
        </div>
      );
    }

    return (
      <div className="min-w-[1000px] flex-1 flex flex-col min-h-0">
        {/* Header - Fixed header layout */}
        <div className="grid grid-cols-[60px_1.5fr_1.2fr_100px_100px_140px_80px] gap-4 px-6 py-4 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 text-sm font-semibold text-zinc-600 capitalize flex-shrink-0">
          <div className="flex items-center justify-center">S.No</div>
          <div className="flex items-center">Title</div>
          <div className="flex items-center">Mentor Name</div>
          <div className="flex items-center justify-center">Profile</div>
          <div className="flex items-center justify-center">Price</div>
          <div className="flex items-center justify-center">Webinar On</div>
          <div className="flex items-center justify-center">Action</div>
        </div>

        <div className="flex-1 relative min-h-0 overflow-y-auto">
          {currentWebinars.map((webinar, index) => {
            const webinarId = webinar.id;
            const hasImageError = imageErrors[webinarId];
            const imageUrl = hasImageError 
              ? 'https://via.placeholder.com/40?text=No+Image'
              : webinar.mentorProfileImage;

            return (
              <div
                key={webinar.id}
                className="grid grid-cols-[60px_1.5fr_1.2fr_100px_100px_140px_80px] gap-4 py-4 px-6 border-b border-zinc-100 hover:bg-zinc-50/50 transition-all duration-200"
              >
                <div className="flex items-center justify-center">
                  <span className="text-sm font-semibold text-zinc-800">{startIndex + index + 1}</span>
                </div>

                <div className="flex items-center min-w-0">
                  <span className="text-sm font-medium text-zinc-900 truncate w-full" title={webinar.title}>{webinar.title}</span>
                </div>

                <div className="flex items-center min-w-0">
                  <span className="text-sm text-zinc-700 font-medium truncate w-full" title={webinar.mentorName}>{webinar.mentorName}</span>
                </div>

                <div className="flex items-center justify-center">
                  <img
                    src={imageUrl}
                    alt={webinar.mentorName}
                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                    onError={() => handleImageError(webinarId)}
                    loading="lazy"
                  />
                </div>

                {/* Price Column */}
                <div className="flex items-center justify-center">
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    webinar.price === 0 || webinar.price === '0' 
                      ? ' text-green-700' 
                      : ' text-amber-700'
                  }`}>
                    {formatPrice(webinar.price)}
                  </span>
                </div>

                <div className="flex items-center justify-center">
                  <span className="text-sm text-zinc-700 font-medium">{formatDate(webinar.webinarOn)}</span>
                </div>

                <div className="flex items-center justify-center">
                  <span className="text-sm text-zinc-400 font-medium">-</span>
                </div>

              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="text-zinc-900 antialiased flex-1 min-h-0 overflow-hidden">
      <main className="max-w-[1400px] mx-auto h-full flex flex-col px-4 py-6 w-full">
          <div className="mb-4 flex-shrink-0">
            <h1 className="text-lg font-bold text-[#1E1B4B]">Webinar History</h1>
          </div>

          <div className="bg-white/70 rounded-xl shadow-sm border border-zinc-100 flex-1 flex flex-col min-h-0 overflow-hidden webinar-history-container">
            <div className="flex-1 flex flex-col min-h-0 overflow-x-auto overflow-y-hidden">
              {getContent()}
            </div>

            {totalItems > 0 && !isLoading && (
              <div className="border-t border-zinc-200 bg-white/50 flex-shrink-0">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </div>
        </main>
      </div>
      <CommonToastContainer />

      <style>{`
          .html-description-preview p {
              display: inline;
          }
          .html-description-preview p + p::before {
              content: " ";
          }
          .html-description-preview ul, .html-description-preview ol {
              display: inline;
              padding: 0;
              margin: 0;
          }
          .html-description-preview li {
              display: inline;
          }
          .html-description-preview li + li::before {
              content: ", ";
          }
      `}</style>
    </>
  );
}